import React, { useState, useEffect } from 'react';
import { XMarkIcon, BoltIcon } from '@heroicons/react/24/outline';

export default function TriggerNodeModal({ isOpen, onClose, node, onUpdate }) {
  const [formData, setFormData] = useState({
    label: '',
    triggerType: 'manual',
    scheduleType: 'daily',
    monthlyDay: 1,
    monthlyTime: '12:00',
    weeklyDay: 'monday',
    weeklyTime: '12:00',
    dailyTime: '12:00'
  });

  useEffect(() => {
    if (node?.data) {
      setFormData({
        label: node.data.label || '',
        triggerType: node.data.triggerType || 'manual',
        scheduleType: node.data.scheduleType || 'daily',
        monthlyDay: node.data.monthlyDay || 1,
        monthlyTime: node.data.monthlyTime || '12:00',
        weeklyDay: node.data.weeklyDay || 'monday',
        weeklyTime: node.data.weeklyTime || '12:00',
        dailyTime: node.data.dailyTime || '12:00'
      });
    }
  }, [node]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const updatedNode = {
      ...node,
      position: node.position,
      data: {
        ...node.data,
        ...formData,
        onDelete: node.data.onDelete,
        onRename: node.data.onRename,
        onUpdateTiming: node.data.onUpdateTiming
      }
    };

    onUpdate(updatedNode);
    onClose();
  };

  if (!isOpen || !node) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-divider rounded-lg p-6 w-full max-w-2xl mx-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <BoltIcon className="w-6 h-6 text-green-500" />
            <h2 className="text-xl font-semibold">Edit Trigger</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="w-full px-3 py-2 bg-dark-300 rounded-lg border border-divider focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Trigger Type</label>
            <select
              value={formData.triggerType}
              onChange={(e) => setFormData({ ...formData, triggerType: e.target.value })}
              className="w-full px-3 py-2 bg-dark-300 rounded-lg border border-divider focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
            >
              <option value="manual">Manual</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>

          {formData.triggerType === 'scheduled' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Schedule Type</label>
                <select
                  value={formData.scheduleType}
                  onChange={(e) => setFormData({ ...formData, scheduleType: e.target.value })}
                  className="w-full px-3 py-2 bg-dark-300 rounded-lg border border-divider focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {formData.scheduleType === 'monthly' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Day of Month</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.monthlyDay}
                      onChange={(e) => setFormData({ ...formData, monthlyDay: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 bg-dark-300 rounded-lg border border-divider focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Time</label>
                    <input
                      type="time"
                      value={formData.monthlyTime}
                      onChange={(e) => setFormData({ ...formData, monthlyTime: e.target.value })}
                      className="w-full px-3 py-2 bg-dark-300 rounded-lg border border-divider focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                    />
                  </div>
                </div>
              )}

              {formData.scheduleType === 'weekly' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Day of Week</label>
                    <select
                      value={formData.weeklyDay}
                      onChange={(e) => setFormData({ ...formData, weeklyDay: e.target.value })}
                      className="w-full px-3 py-2 bg-dark-300 rounded-lg border border-divider focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                    >
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Time</label>
                    <input
                      type="time"
                      value={formData.weeklyTime}
                      onChange={(e) => setFormData({ ...formData, weeklyTime: e.target.value })}
                      className="w-full px-3 py-2 bg-dark-300 rounded-lg border border-divider focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                    />
                  </div>
                </div>
              )}

              {formData.scheduleType === 'daily' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Time</label>
                  <input
                    type="time"
                    value={formData.dailyTime}
                    onChange={(e) => setFormData({ ...formData, dailyTime: e.target.value })}
                    className="w-full px-3 py-2 bg-dark-300 rounded-lg border border-divider focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                  />
                </div>
              )}
            </>
          )}

          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 